import { notificationOpenAction } from "@Actions/Notification/action";
import enterpriseApi from "@Apis/Enterprise/api";
import ninetickBillingApi from "@Apis/NinetickBilling/api";
import { NOTIFICATION_TYPE } from "@Constants/common";
import { ENTERPRISE_ID, ENTERPRISE_NAME } from "@Constants/localStorage";
import {
  Button,
  Chip,
  CircularProgress,
  Grid,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
} from "@material-ui/core";
import React, { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import "./style.scss";

type Transaction = {
  id?: string;
  amount?: number;
  currency?: string;
  description?: string;
  paymentType?: string;
  status?: string;
  statusDetail?: {
    title?: string;
    code?: string;
  };
  createdAt?: string;
  custom?: {
    service?: string;
    feature?: string;
    action?: string;
    businessId?: string;
    planCode?: string;
    planName?: string;
    emailMonthlyLimit?: number;
    emailCreditQuantity?: number;
  };
};

const statusLabels: Record<string, string> = {
  pending: "Chờ duyệt",
  approved: "Đã duyệt",
  paid: "Đã thanh toán",
  success: "Thành công",
  cancelled: "Đã huỷ",
  failed: "Thất bại",
};

function formatNumber(value?: number) {
  return Number(value || 0).toLocaleString("vi-VN");
}

function formatMoney(value?: number, currency = "VND") {
  return `${formatNumber(value)} ${currency}`;
}

function formatDate(value?: string) {
  if (!value) return "-";
  return new Date(value).toLocaleString("vi-VN", {
    hour: "2-digit",
    minute: "2-digit",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function getTransactionTitle(transaction: Transaction) {
  if (transaction.custom?.action === "credit") {
    return `Mua ${formatNumber(transaction.custom?.emailCreditQuantity)} email credit`;
  }
  return transaction.custom?.planName || transaction.description || transaction.custom?.planCode || "-";
}

function getStatusClass(status?: string) {
  if (["approved", "paid", "success"].includes(status || "")) return "success";
  if (["cancelled", "failed"].includes(status || "")) return "danger";
  return "pending";
}

const NinetickBilling = () => {
  const dispatch = useDispatch();
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(false);
  const [transactionLoading, setTransactionLoading] = useState(false);
  const [submitting, setSubmitting] = useState("");
  const [creditQuantity, setCreditQuantity] = useState(500);
  const [business, setBusiness] = useState<any>(null);

  const enterpriseName = localStorage.getItem(ENTERPRISE_NAME) || business?.name || "Business";
  const businessId = localStorage.getItem(ENTERPRISE_ID) || "";

  const emailBilling = business?.ninetickBilling?.features?.email || business?.emailBilling || {};
  const monthlyLimit = Number(emailBilling?.monthlyLimit || 100);
  const used = Number(emailBilling?.used || 0);
  const creditBalance = Number(emailBilling?.creditBalance || 0);
  const remaining = Math.max(monthlyLimit - used, 0) + creditBalance;
  const usagePercent = monthlyLimit ? Math.min(Math.round((used / monthlyLimit) * 100), 100) : 0;

  const currentPlanName = emailBilling?.plan?.name || "Free";

  const loadTransactions = useCallback(async () => {
    if (!businessId) return;
    setTransactionLoading(true);
    try {
      const response = await ninetickBillingApi.getTransactions({
        page: 1,
        limit: 8,
        "custom.businessId": businessId,
      });
      const result = Array.isArray(response?.result) ? response.result : [];
      setTransactions(result.filter((item: Transaction) => (
        ["ninetick", "ninetick-email"].includes(item.custom?.service || "") &&
        item.custom?.action === "credit"
      )));
    } catch (error) {
      console.log(error);
      setTransactions([]);
    } finally {
      setTransactionLoading(false);
    }
  }, [businessId]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [businessRes] = await Promise.allSettled([
        enterpriseApi.getInfoEnterprise({}),
      ]);

      if (businessRes.status === "fulfilled") {
        setBusiness(businessRes.value?.result || null);
      }
      await loadTransactions();
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  }, [loadTransactions]);

  async function createCreditOrder() {
    if (!businessId || creditQuantity < 1) return;
    setSubmitting("credit");
    try {
      await ninetickBillingApi.createOrder({
        businessId,
        feature: "email",
        action: "credit",
        creditQuantity,
        paymentType: "bank-transfer",
      });
      dispatch(notificationOpenAction({
        isNotification: true,
        typeNotification: NOTIFICATION_TYPE.SUCCESS as "success",
        message: "Đã tạo đơn mua credit",
        description: "Admin sẽ duyệt giao dịch sau khi xác thực thanh toán.",
      }));
      await loadTransactions();
    } catch (error: any) {
      dispatch(notificationOpenAction({
        isNotification: true,
        typeNotification: NOTIFICATION_TYPE.ERROR as "error",
        message: "Không tạo được đơn",
        description: error?.response?.data?.message || "Vui lòng kiểm tra hồ sơ thanh toán hoặc thử lại.",
      }));
    } finally {
      setSubmitting("");
    }
  }

  useEffect(() => {
    loadData();
  }, [loadData]);

  return (
    <div className="NinetickBillingPage">
      <div className="NinetickBillingPage__inner">
        <div className="billingHeader">
          <div>
            <span className="billingHeader__eyebrow">Ninetick services</span>
            <h1>Gói dịch vụ</h1>
            <p>{enterpriseName}</p>
          </div>
          <Button
            className="billingHeader__refresh"
            variant="outlined"
            disabled={loading || transactionLoading}
            onClick={loadData}
          >
            {loading ? <CircularProgress size={18} /> : "Làm mới"}
          </Button>
        </div>

        <div className="billingOverview">
          <div className="currentPlan">
            <div className="currentPlan__top">
              <div>
                <span>Gói hiện tại</span>
                <strong>{currentPlanName}</strong>
              </div>
              <Chip label={`${usagePercent}% đã dùng`} className="usageChip" />
            </div>
            <LinearProgress className="usageBar" variant="determinate" value={usagePercent} />
            <div className="currentPlan__bottom">
              <span>{formatNumber(used)} / {formatNumber(monthlyLimit)} email tháng này</span>
              <span>Còn lại {formatNumber(remaining)} email</span>
            </div>
          </div>

          <Grid container spacing={2} className="metricGrid">
            <Grid item xs={12} md={4}>
              <div className="metric">
                <span>Hạn mức tháng</span>
                <strong>{formatNumber(monthlyLimit)}</strong>
              </div>
            </Grid>
            <Grid item xs={12} md={4}>
              <div className="metric">
                <span>Credit còn lại</span>
                <strong>{formatNumber(creditBalance)}</strong>
              </div>
            </Grid>
            <Grid item xs={12} md={4}>
              <div className="metric">
                <span>Giao dịch gần đây</span>
                <strong>{formatNumber(transactions.length)}</strong>
              </div>
            </Grid>
          </Grid>
        </div>

        <div className="billingSection">
          <div className="creditPanel">
            <div className="creditPanel__copy">
              <span>Mua thêm</span>
              <h2>Credit email</h2>
              <p>Credit được trừ sau khi business dùng hết hạn mức email trong gói tháng.</p>
            </div>
            <div className="creditPanel__form">
              <TextField
                label="Số email mua thêm"
                type="number"
                variant="outlined"
                value={creditQuantity}
                onChange={(event) => setCreditQuantity(Math.max(Number(event.target.value || 0), 0))}
              />
              <div className="creditPanel__price">
                <span>Tạm tính</span>
                <strong>{formatMoney(creditQuantity * 500)}</strong>
              </div>
              <Button
                color="primary"
                variant="contained"
                disabled={Boolean(submitting) || creditQuantity < 1}
                onClick={createCreditOrder}
              >
                {submitting === "credit" ? <CircularProgress size={20} /> : "Tạo đơn mua credit"}
              </Button>
            </div>
          </div>
        </div>

        <div className="billingSection transactionsPanel">
          <div className="sectionTitle sectionTitle--inline">
            <div>
              <h2>Danh sách giao dịch</h2>
              <p>Theo dõi các đơn mua credit của business này.</p>
            </div>
            {transactionLoading && <CircularProgress size={22} />}
          </div>
          <div className="transactionsTable">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Giao dịch</TableCell>
                  <TableCell>Loại</TableCell>
                  <TableCell>Thanh toán</TableCell>
                  <TableCell>Trạng thái</TableCell>
                  <TableCell>Ngày tạo</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {transactions.map((transaction) => {
                  const status = transaction.status || transaction.statusDetail?.code || "pending";
                  return (
                    <TableRow key={transaction.id || `${transaction.createdAt}-${transaction.amount}`}>
                      <TableCell>
                        <div className="transactionName">
                          <strong>{getTransactionTitle(transaction)}</strong>
                          <span>{formatMoney(transaction.amount, transaction.currency)}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        {transaction.custom?.action === "credit" ? "Mua credit" : "Mua gói"}
                      </TableCell>
                      <TableCell>{transaction.paymentType || "bank-transfer"}</TableCell>
                      <TableCell>
                        <Chip
                          label={statusLabels[status] || transaction.statusDetail?.title || status}
                          className={`statusChip statusChip--${getStatusClass(status)}`}
                        />
                      </TableCell>
                      <TableCell>{formatDate(transaction.createdAt)}</TableCell>
                    </TableRow>
                  );
                })}
                {!transactions.length && !transactionLoading && (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <div className="emptyTransactions">
                        Chưa có giao dịch mua credit cho business này.
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NinetickBilling;
